import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginResponse } from '@/features/auth/dtos/auth.dto'
import { PermissionChecker, type PermissionBit } from '@/shared/constants/permissions.constants'
import { GOOGLE_OAUTH_CONFIG, buildOAuthURL, generateOAuthState } from '@/core/config/oauth.config'
import { MODULE_IDS } from '@/core/config/oauth.constants'
import { getApiErrorMessage, logFallback } from '@/shared/utils/error-handler'
import {
  AUTH_PROVIDERS,
  POPUP_CHECK_INTERVAL_MS,
  STORAGE_KEYS,
} from '@/core/constants/auth.constants'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!user.value && !!token.value)

  const permissionChecker = computed(() => {
    if (!user.value) return new PermissionChecker(0)
    return new PermissionChecker(user.value.permissions ?? 0)
  })

  function setUser(userData: User) {
    user.value = userData
  }

  function setToken(authToken: string) {
    token.value = authToken
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken)
  }

  function setLoading(isLoading: boolean) {
    loading.value = isLoading
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage
  }

  function clearError() {
    error.value = null
  }

  function can(permission: PermissionBit): boolean {
    return permissionChecker.value.can(permission)
  }

  function canAll(...permissions: PermissionBit[]): boolean {
    return permissionChecker.value.canAll(...permissions)
  }

  function canAny(...permissions: PermissionBit[]): boolean {
    return permissionChecker.value.canAny(...permissions)
  }

  async function getPermissionsBitmap(
    rawPermissions: Array<{ scope_custom_id: number; roles: string[] }>
  ): Promise<number> {
    const { getBackofficeRoles } = await import('@/features/auth/queries/auth.queries')
    const { resolvePermissionsFromRoles } = await import('@/features/auth/adapters/auth.adapter')

    const attempt = async () => {
      const rolesResponse = await getBackofficeRoles()
      const roles = rolesResponse?.results ?? []
      if (roles.length === 0) {
        throw new Error('No se pudieron cargar los permisos del backoffice')
      }
      return resolvePermissionsFromRoles(rawPermissions, roles)
    }

    try {
      return await attempt()
    } catch {
      await new Promise((r) => setTimeout(r, 300))
      return await attempt()
    }
  }

  async function resolveAndSetPermissions(userData: User, response: LoginResponse) {
    userData.permissions = await getPermissionsBitmap(response.permissions)
  }

  async function refreshPermissions() {
    if (!user.value || !token.value || !user.value.rawPermissions) return
    try {
      const bitmap = await getPermissionsBitmap(user.value.rawPermissions)
      user.value.permissions = bitmap
      localStorage.setItem(STORAGE_KEYS.LOGGED_USER, JSON.stringify(user.value))
    } catch (err) {
      logFallback('permissions', err, 'Failed to refresh backoffice roles')
    }
  }

  async function loginWithEmail(email: string, password: string) {
    setLoading(true)
    clearError()

    try {
      const { login: apiLogin, getMyUser } = await import('@/features/auth/queries/auth.queries')
      const { adaptLoginResponseToUser } = await import('@/features/auth/adapters/auth.adapter')

      const response = await apiLogin({
        username: email,
        password: password,
        login_type: AUTH_PROVIDERS.TOTEAT,
        scope: 'backoffice',
      })

      if (response.access_token) {
        const userData = adaptLoginResponseToUser(response, email)

        setToken(response.access_token)

        try {
          const userProfile = await getMyUser(response.access_token)
          if (userProfile) {
            userData.id = userProfile.legacy_id || userProfile.id || 0
            userData.firstname = userProfile.firstname || ''
            userData.lastname = userProfile.lastname || ''
            userData.name = userProfile.name || userData.name
          }
        } catch {}

        await resolveAndSetPermissions(userData, response)

        setUser(userData)
        localStorage.setItem(STORAGE_KEYS.LOGGED_USER, JSON.stringify(userData))
        localStorage.setItem(STORAGE_KEYS.EMAIL, email)

        return true
      } else {
        setError('Usuario o contraseña incorrecta')
        return false
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Login failed')
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  async function loginWithGoogle() {
    setLoading(true)
    clearError()

    try {
      const state = generateOAuthState(MODULE_IDS.BACKOFFICE)
      localStorage.setItem(STORAGE_KEYS.TOT2, state)
      localStorage.setItem(STORAGE_KEYS.LOGIN_TYPE, '2')

      const oauthURL = buildOAuthURL(GOOGLE_OAUTH_CONFIG, state)

      const popup = window.open(oauthURL, '_blank', 'width=600,height=700')

      if (!popup) {
        setError('Popup bloqueado. Por favor habilite los popups para este sitio.')
        setLoading(false)
        return false
      }

      return new Promise<boolean>((resolve) => {
        let resolved = false

        const handleMessage = async (event: MessageEvent) => {
          const originDomain = event.origin.substring(event.origin.length - 11)
          if (originDomain !== '.toteat.com' && !event.origin.includes('localhost')) {
            return
          }

          try {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data

            if (data.codigo && data.estado) {
              if (resolved) {
                return
              }
              resolved = true

              const savedState = localStorage.getItem(STORAGE_KEYS.TOT2)
              if (savedState !== data.estado) {
                setError('Error de seguridad: estado no coincide')
                window.removeEventListener('message', handleMessage)
                clearInterval(popupInterval)
                setLoading(false)
                resolve(false)
                return
              }

              localStorage.setItem(STORAGE_KEYS.TOT0, data.codigo)

              if (popup && !popup.closed) {
                popup.close()
              }

              window.removeEventListener('message', handleMessage)
              clearInterval(popupInterval)

              const success = await verifyOAuthLogin(data.codigo)
              resolve(success)
            }
          } catch {
            if (!resolved) {
              resolved = true
              setError('Error procesando callback de Google')
              window.removeEventListener('message', handleMessage)
              clearInterval(popupInterval)
              setLoading(false)
              resolve(false)
            }
          }
        }

        window.addEventListener('message', handleMessage)

        const popupInterval = setInterval(() => {
          if (popup && popup.closed) {
            if (!resolved) {
              resolved = true
              clearInterval(popupInterval)
              window.removeEventListener('message', handleMessage)
              setLoading(false)
              resolve(false)
            }
          }
        }, POPUP_CHECK_INTERVAL_MS)
      })
    } catch (err) {
      const message = getApiErrorMessage(err, 'Google login failed')
      setError(message)
      setLoading(false)
      return false
    }
  }

  async function verifyOAuthLogin(code: string) {
    try {
      const { login: apiLogin, getMyUser } = await import('@/features/auth/queries/auth.queries')
      const { adaptLoginResponseToUser } = await import('@/features/auth/adapters/auth.adapter')

      const savedLoginType = localStorage.getItem(STORAGE_KEYS.LOGIN_TYPE) || '2'
      const loginType =
        savedLoginType === '1'
          ? AUTH_PROVIDERS.FACEBOOK
          : savedLoginType === '2'
            ? AUTH_PROVIDERS.GOOGLE
            : AUTH_PROVIDERS.TOTEAT

      const decodedCode = decodeURIComponent(code)

      const response = await apiLogin({
        login_type: loginType,
        scope: 'backoffice',
        oauth_token: decodedCode,
        redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
      })

      if (response.access_token) {
        const userData = adaptLoginResponseToUser(response)

        setToken(response.access_token)

        try {
          const userProfile = await getMyUser(response.access_token)
          if (userProfile) {
            userData.id = userProfile.legacy_id || userProfile.id || 0
            userData.firstname = userProfile.firstname || ''
            userData.lastname = userProfile.lastname || ''
            userData.name = userProfile.name || userData.name
          }
        } catch {}

        await resolveAndSetPermissions(userData, response)

        setUser(userData)
        localStorage.setItem(STORAGE_KEYS.LOGGED_USER, JSON.stringify(userData))

        return true
      } else {
        setError('Error iniciando sesión con Google')
        return false
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'OAuth login failed')
      setError(message)
      return false
    } finally {
      setLoading(false)
    }
  }

  async function logout() {
    try {
      if (token.value) {
        const { logout: apiLogout } = await import('@/features/auth/queries/auth.queries')
        await apiLogout()
      }
    } catch {
    } finally {
      user.value = null
      token.value = null
      error.value = null

      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.LOGGED_USER)
      localStorage.removeItem(STORAGE_KEYS.TOT0)
      localStorage.removeItem(STORAGE_KEYS.TOT2)
      localStorage.removeItem(STORAGE_KEYS.LOGIN_TYPE)
      localStorage.removeItem(STORAGE_KEYS.EMAIL)
    }
  }

  function initializeAuth() {
    const savedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    const savedUser = localStorage.getItem(STORAGE_KEYS.LOGGED_USER)

    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser)

        const now = new Date().getTime()
        const lastConnected = userData.lastConnected
          ? new Date(userData.lastConnected).getTime()
          : 0
        const expiresIn = userData.expiresIn || 3600000

        if (now - lastConnected < expiresIn) {
          user.value = userData
          token.value = savedToken

          userData.lastConnected = new Date()
          localStorage.setItem(STORAGE_KEYS.LOGGED_USER, JSON.stringify(userData))
        } else {
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
          localStorage.removeItem(STORAGE_KEYS.LOGGED_USER)
        }
      } catch {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.LOGGED_USER)
      }
    }
  }

  function isSessionExpired(): boolean {
    if (!user.value || !user.value.expiresIn || !user.value.lastConnected) return true
    const now = new Date().getTime()
    const lastConnected = new Date(user.value.lastConnected).getTime()
    return now - lastConnected >= user.value.expiresIn
  }

  function getSessionExpiresAt(): number | null {
    if (!user.value || !user.value.expiresIn || !user.value.lastConnected) return null
    const lastConnected = new Date(user.value.lastConnected).getTime()
    return lastConnected + user.value.expiresIn
  }

  return {
    user,
    token,
    loading,
    error,

    isAuthenticated,
    permissionChecker,

    setUser,
    setToken,
    setLoading,
    setError,
    clearError,
    can,
    canAll,
    canAny,
    loginWithEmail,
    loginWithGoogle,
    logout,
    initializeAuth,
    isSessionExpired,
    getSessionExpiresAt,
    refreshPermissions,
  }
})
